/**
 * <SettingRow> — une ligne de réglage (molecule).
 *
 * Icône, libellé, valeur secondaire, chevron : la ligne des écrans de réglages (Gestion,
 * Facturation, date de prélèvement). `danger` peint le libellé et son icône en rouge — c'est
 * la ligne qui résilie, pas une ligne comme les autres.
 *
 * Pas de chrome : comme `<Checkbox>`, la ligne ne dessine ni fond ni bordure. Un groupe de
 * réglages se pose dans `<SettingsSection>` : une carte, un filet entre les lignes.
 *
 * `trailing` est l'emplacement de fin : ce qu'une valeur en texte ne sait pas dire (une bascule,
 * une barre d'avancement, une chip). Sans `onPress`, la ligne n'est plus un bouton : elle porte
 * un réglage que son `trailing` commande, ou une information, et n'a pas de chevron.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { FocusRing, focusRingHost } from "../../atoms/FocusRing"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Loader } from "../../atoms/Loader"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean }

/** Hauteur de touche minimale — la même que le bouton du livre. Son squelette la reprend. */
export const SETTING_ROW_MIN_HEIGHT = 44

export type SettingRowProps = {
    /** Glyphe de tête. Absent : la ligne commence au libellé (une liste de bascules). */
    iconName?: IconName
    label: string
    /** Une phrase sous le libellé, à l'encre secondaire. Rien par défaut. */
    description?: string
    /** Valeur secondaire à droite — « le 5 », « Visa •••• 4242 ». */
    value?: string
    /**
     * Emplacement de fin, après la valeur : une bascule, une barre d'avancement, une chip. Rien
     * par défaut. Un contrôle posé ici (un `<Switch>`) demande une ligne sans `onPress`.
     */
    trailing?: React.ReactNode
    /** Chevron de fin — défaut `true` sur une ligne qui presse. À couper quand elle n'ouvre rien. */
    chevron?: boolean
    /** Ligne destructrice (résilier, supprimer) : libellé et icône en rouge. */
    danger?: boolean
    /** Geste en cours : la valeur laisse la place au rond d'attente et la ligne ne répond plus. */
    loading?: boolean
    /**
     * La ligne est le choix en cours d'une liste de choix (un fuseau parmi d'autres) : annoncé aux
     * lecteurs d'écran. L'icône, elle, est choisie par l'appelant (une coche). Défaut : `false`.
     */
    selected?: boolean
    /** Absent : la ligne ne presse pas (elle porte son réglage dans `trailing`, ou une information que le parent ne règle pas lui-même). */
    onPress?: () => void
    testID?: string
}

export function SettingRow(props: SettingRowProps) {
    const {
        iconName,
        label,
        description,
        value,
        trailing,
        danger = false,
        loading = false,
        selected = false,
        onPress,
        testID,
    } = props
    const chevron = props.chevron ?? onPress != null

    const { theme } = useUnistyles()
    const accent: SemanticColorKey = danger ? "text-danger" : "primary"
    const labelColor: SemanticColorKey = danger ? "text-danger" : "text-strong"
    // Le nom que lit un lecteur d'écran : la ligne se dit d'un trait, sa phrase comprise
    // (« libellé, description : valeur »). Sans description, celui d'avant.
    const titled = description != null ? `${label}, ${description}` : label
    const accessibleName = value != null ? `${titled} : ${value}` : titled

    const labelText = (
        <Text
            size="text-base"
            weight="semibold"
            color={labelColor}
            style={description == null ? styles.label : undefined}
        >
            {label}
        </Text>
    )

    const content = (
        <Row gap="3" align="center">
            {iconName != null && <Icon name={iconName} size={20} color={accent} />}
            {description == null ? (
                labelText
            ) : (
                <Stack gap="0" style={styles.labelColumn}>
                    {labelText}
                    <Text size="text-sm" color="text-muted">
                        {description}
                    </Text>
                </Stack>
            )}
            {loading ? (
                <Loader size="small" color={accent} />
            ) : (
                <>
                    {value != null && (
                        <Text size="text-md" color="text-muted" numberOfLines={1} style={styles.value}>
                            {value}
                        </Text>
                    )}
                    {trailing}
                </>
            )}
            {chevron && <Icon name="chevron-right" size={16} color="text-muted" />}
        </Row>
    )

    if (onPress == null) {
        // Une ligne qui se lit seulement se dit d'un trait (« libellé : valeur ») ; une ligne qui
        // porte un contrôle (`trailing`) le laisse se présenter lui-même au lecteur d'écran.
        const grouped = trailing == null
        return (
            <View
                testID={testID}
                style={styles.root}
                accessible={grouped}
                accessibilityRole={grouped ? "text" : undefined}
                accessibilityLabel={grouped ? accessibleName : undefined}
            >
                {content}
            </View>
        )
    }

    return (
        <Pressable
            testID={testID}
            disabled={loading}
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={accessibleName}
            accessibilityState={{ disabled: loading, busy: loading, selected: selected || undefined }}
            style={({ pressed }: PressableVisualState) => [
                styles.root,
                focusRingHost,
                pressed && !loading ? styles.pressed : undefined,
            ]}
        >
            {({ focused }: PressableVisualState) => (
                <>
                    <FocusRing visible={focused === true && !loading} radius={theme.radius.md} />
                    {content}
                </>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        minHeight: SETTING_ROW_MIN_HEIGHT,
        justifyContent: "center",
        paddingVertical: theme.spacing["2"],
    },
    pressed: { opacity: 0.6 },
    label: { flexShrink: 1, flexGrow: 1 },
    /**
     * Le libellé et sa phrase prennent la place qui reste, à partir de zéro : une phrase longue
     * passe à la ligne au lieu de demander sa largeur entière et de comprimer les glyphes voisins.
     */
    labelColumn: { flex: 1 },
    /**
     * La valeur ne se réduit pas : elle est courte (« le 5 », « •••• 4242 ») et c'est sa fin qui
     * porte l'information. C'est le libellé qui se replie quand la ligne manque de place.
     */
    value: { flexShrink: 0 },
}))
